import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '../../auth/auth.service';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class AuthGuard implements CanActivate {
    constructor(private readonly router: Router, private readonly authService: AuthService) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> {
        const token = this.authService.getToken();

        // ❌ No token → redirect to login with returnUrl
        if (!token) {
            this.redirectToLogin(state.url);
            return false;
        }

        // ✅ Token exists and is NOT expired
        if (!this.authService.isTokenExpired(token)) {
            return true;
        }

        // 🔄 Token expired → try refresh
        return this.authService.refreshTokenIfNeeded().pipe(
            map((refreshSuccessful) => {
                if (refreshSuccessful) {
                    return true;
                }

                // ❌ Refresh failed → clear data & redirect
                this.clearSession();
                this.redirectToLogin(state.url);
                return false;
            }),
            catchError(() => {
                this.clearSession();
                this.redirectToLogin(state.url);
                return of(false);
            })
        );
    }

    // 🔹 Helper: Redirect with returnUrl
    private redirectToLogin(returnUrl: string): void {
        this.router.navigate(['/login'], {
            queryParams: { returnUrl },
        });
    }

    // 🔹 Helper: साफ session
    private clearSession(): void {
        this.authService.removeToken();
        this.authService.removeVendorData();
        this.authService.removeTenantData();
    }
}
